import {PackageStatus, PackageType} from '@sollapay/enums';
import {cn} from '@sollapay/ui';
import {
  Alert,
  Badge,
  Icon,
  ModalTabIconLabel,
  MultiStepsModal,
  Spinner,
  Typography
} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {
  ShowErrorsContext,
  useActivationPackageFormModal,
  useClarificationResubmit
} from '@/features/trust-accounts/activation/hooks';
import {
  TrustAccountActivationComplianceForm,
  TrustAccountActivationDetailsForm,
  TrustAccountActivationKycForm
} from '@/features/trust-accounts/activation/packages';
import {
  useTrustAccountPermissions,
  useTrustTypeStrategy
} from '@/features/trust-accounts/providers';

import {
  ActivationPackageStepsNav,
  ClarificationBanner,
  ClarificationResubmitted,
  PackageTabBadge
} from '../components';
import {
  ACTIVATION_PACKAGE_CANCEL_BUTTON_METADATA,
  ACTIVATION_PACKAGE_SAVE_BUTTON_METADATA,
  ComplianceStep,
  getStepsMapForTab,
  TrustDetailsStep
} from '../config';
import {
  getPackageClarificationNote,
  getReservedClarificationNoteKey,
  isComplianceFormReadOnly,
  isPackageActionable,
  isPackageReadOnly
} from '../utils';

import type {ActivationPackageViewModel} from '../hooks';
import type {CustomButtonConfig, TabItem} from '@sollapay/ui/components';
import type {FC} from 'react';

type ActivationPackageFormModalProps = {
  trustAccountId: string;
  isOpen: boolean;
  onClose: () => void;
  initialTab?: PackageType;
  initialStep?: string;
  packages?: ActivationPackageViewModel[];
  disableFocusTrap?: boolean;
  isViewOnly?: boolean;
  isActivationLocked?: boolean;
  showErrors?: boolean;
};

export const ActivationPackageFormModal: FC<ActivationPackageFormModalProps> = ({
  trustAccountId,
  isOpen,
  onClose,
  initialTab,
  initialStep,
  packages = [],
  disableFocusTrap = false,
  isViewOnly = false,
  isActivationLocked = false,
  showErrors = true
}) => {
  const {canSignDeclarations} = useTrustAccountPermissions();
  const {t} = useTranslation(['trustAccounts', 'common']);
  const strategy = useTrustTypeStrategy();
  const {
    tabsData,
    activeTab,
    activeStep,
    formState,
    statusByStep,
    onFormStateChange,
    onStepStatus,
    handleTabChange,
    handleStepChange,
    handleNextClick,
    handleBackClick,
    navigateTo,
    isLastStepOfLastTab,
    isFirstStepOfFirstTab
  } = useActivationPackageFormModal(initialTab, initialStep);

  const {
    activePackage,
    clarifiedPackage,
    canResubmit,
    isResubmitting,
    didResubmitFail,
    resubmittedType,
    isResubmitted,
    resubmit,
    reset: resetResubmit
  } = useClarificationResubmit({trustAccountId, packages, activeTab});

  const tabs: TabItem[] = tabsData.map(({value, icon, labelKey, comingSoon}) => {
    const pkg = packages.find(p => p.type === value);
    const isTabResubmitted = resubmittedType === value;
    const hasClarification = pkg?.status === PackageStatus.CLARIFICATION_REQUIRED;
    const completed = (pkg?.readiness.isReady ?? false) && !hasClarification && !isTabResubmitted;
    return {
      value,
      disabled: comingSoon,
      label: (
        <ModalTabIconLabel
          icon={icon}
          text={t(labelKey)}
          completed={completed}
          disabled={comingSoon}
          badge={
            comingSoon ? (
              <Badge variant="neutral" size="sm">
                {t('common:comingSoon')}
              </Badge>
            ) : (
              <PackageTabBadge status={pkg?.status} isResubmitted={isTabResubmitted} />
            )
          }
        />
      )
    };
  });

  const activeStepsMap = getStepsMapForTab(activeTab, strategy);

  // Documents normally lock at the package level (canEdit), independent of the whole-modal
  // lock — a package under CLARIFICATION_REQUIRED stays editable even while the account
  // is submitted. `isViewOnly` always wins on top of that package-level nuance, so a viewer
  // with no write permission can never upload/delete.
  const withModalReadOnly = (packageLevelReadOnly: boolean) => packageLevelReadOnly || isViewOnly;

  const trustDetailsPackage = packages.find(p => p.type === PackageType.TRUST_DETAILS);
  const compliancePackage = packages.find(p => p.type === PackageType.COMPLIANCE);
  const trustDetailsDocumentsReadOnly = withModalReadOnly(
    !isPackageActionable(trustDetailsPackage)
  );
  const complianceDocumentsReadOnly = withModalReadOnly(!isPackageActionable(compliancePackage));

  // Transaction legal documents are part of the compliance package, so they lock with it:
  // read-only on a submitted account except while the package is back with the firm.
  const transactionLegalDocumentsReadOnly = isPackageReadOnly(compliancePackage, {
    isActivationLocked,
    isViewOnly
  });

  const isTabReadOnly = isPackageReadOnly(activePackage, {isActivationLocked, isViewOnly});

  const {isDirty, isLoading, isError, isSaving, onSave, onCancel} = formState;
  const isSellerRosterStep =
    activeTab === PackageType.TRUST_DETAILS &&
    activeStep === TrustDetailsStep.SELLER_DETAILS &&
    strategy.allowsMultipleSellerSideParties;

  const reservedNoteKey = getReservedClarificationNoteKey(
    clarifiedPackage?.clarificationRequests ?? null
  );

  // Every way out resets the resubmit state: the modal stays mounted, so a stale "resubmitted"
  // confirmation would otherwise reappear when the package comes back for another round.
  const closeModal = () => {
    resetResubmit();
    onClose();
  };

  // Dismissing mid save-and-resubmit would let the pending continuation resubmit and restore the
  // confirmation after the reset, so the modal stays put until the operation settles.
  const handleOpenChange = (open: boolean) => {
    if (open || isSaving || isResubmitting) return;
    onCancel();
    closeModal();
  };

  const saveButtons = [
    {
      label: t(ACTIVATION_PACKAGE_CANCEL_BUTTON_METADATA.labelKey),
      icon: <Icon name="close" width={20} height={20} />,
      isDestructive: ACTIVATION_PACKAGE_CANCEL_BUTTON_METADATA.isDestructive,
      isHidden: !isDirty || isSaving,
      onClick: () => onCancel()
    } satisfies CustomButtonConfig,
    {
      label: t(ACTIVATION_PACKAGE_SAVE_BUTTON_METADATA.labelKey),
      icon: <Icon name="save" width={20} height={20} />,
      isDisabled: !isDirty,
      isLoading: isSaving,
      onClick: () => onSave()
    } satisfies CustomButtonConfig
  ];

  // Like Continue: pending edits on the step are saved first, and a failed save stops here.
  const handleResubmit = async () => {
    if (isDirty) {
      try {
        await onSave();
      } catch {
        return;
      }
    }
    resubmit();
  };

  const nextButtonProps = canResubmit
    ? {
        nextLabel: t('activation.clarification.resubmit'),
        nextHideIcon: true,
        nextIsLoading: isResubmitting || isSaving,
        // `canSubmit` reflects what is saved, so unsaved edits may be what makes it ready.
        nextDisabled: !clarifiedPackage?.canSubmit && !isDirty,
        onNextClick: handleResubmit
      }
    : {
        nextLabel: isLastStepOfLastTab
          ? t('activation.packageFormModal.finish')
          : t('activation.packageFormModal.next'),
        nextHideIcon: isLastStepOfLastTab,
        nextIsLoading: !isLastStepOfLastTab && isSaving,
        nextDisabled: !isLastStepOfLastTab && isSaving,
        onNextClick: isLastStepOfLastTab ? closeModal : handleNextClick
      };

  return (
    <ShowErrorsContext.Provider value={showErrors}>
      <MultiStepsModal
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        isDirty={!isTabReadOnly && isDirty}
        title={t('activation.packageFormModal.title')}
        disableFocusTrap={disableFocusTrap}
        tabs={tabs}
        activeTab={activeTab}
        scrollResetKey={activeStep}
        isBodyFlush={isSellerRosterStep}
        onTabChange={(tab: string) => handleTabChange(tab as PackageType)}
        {...nextButtonProps}
        backLabel={t('activation.packageFormModal.back')}
        hideFooter={isResubmitted}
        onBackClick={isFirstStepOfFirstTab ? undefined : handleBackClick}
        subHeader={
          Object.keys(activeStepsMap).length > 0 && !isResubmitted ? (
            <ActivationPackageStepsNav
              items={activeStepsMap}
              value={activeStep}
              onChange={handleStepChange}
              statusMap={statusByStep}
              showErrors={showErrors}
            />
          ) : undefined
        }
        customButtons={isTabReadOnly ? [] : saveButtons}
      >
        <div className="flex flex-col">
          {isResubmitted && (
            <ClarificationResubmitted
              packageLabel={t(tabsData.find(tab => tab.value === activeTab)?.labelKey ?? '')}
              onDone={closeModal}
            />
          )}
          {clarifiedPackage && !isResubmitted && (
            <div className="flex flex-col gap-3 px-6 pt-4" data-slot="clarification-section">
              <ClarificationBanner
                round={clarifiedPackage.clarificationRound}
                note={
                  reservedNoteKey
                    ? t(reservedNoteKey)
                    : getPackageClarificationNote(clarifiedPackage)
                }
                reviewerName={clarifiedPackage.clarificationRequestedBy}
                requestedAt={clarifiedPackage.clarificationRequestedAt}
              />
              {didResubmitFail && (
                <Alert variant="error" description={t('activation.clarification.error')} />
              )}
            </div>
          )}
          <div
            hidden={isResubmitted}
            className={isSellerRosterStep ? undefined : 'py-4 px-6'}
            data-slot="activation-package-form-body"
          >
            {isLoading && (
              <div
                className={cn(
                  'flex items-center gap-2 rounded-xl border border-border bg-background-surface p-6',
                  isSellerRosterStep && 'm-6'
                )}
              >
                <Spinner className="text-fg-tertiary" />
                <Typography as="p" size="sm" color="tertiary">
                  {t('common:loading')}
                </Typography>
              </div>
            )}

            {isError && (
              <div
                className={cn(
                  'rounded-xl border border-border bg-background-surface p-6',
                  isSellerRosterStep && 'm-6'
                )}
              >
                <Typography as="p" size="sm" className="text-destructive">
                  {t('errorLoadingDetails')}
                </Typography>
              </div>
            )}

            <div hidden={isLoading || isError}>
              {activeTab === PackageType.TRUST_DETAILS && (
                <TrustAccountActivationDetailsForm
                  trustAccountId={trustAccountId}
                  activeStep={activeStep as TrustDetailsStep}
                  readOnly={isTabReadOnly}
                  documentsReadOnly={trustDetailsDocumentsReadOnly}
                  onFormStateChange={onFormStateChange}
                  onStepStatus={onStepStatus}
                />
              )}
              {activeTab === PackageType.COMPLIANCE && (
                <TrustAccountActivationComplianceForm
                  trustAccountId={trustAccountId}
                  activeStep={activeStep as ComplianceStep}
                  readOnly={isComplianceFormReadOnly({
                    readOnly: isTabReadOnly,
                    canSignDeclarations
                  })}
                  documentsReadOnly={complianceDocumentsReadOnly}
                  transactionLegalDocumentsReadOnly={transactionLegalDocumentsReadOnly}
                  onFormStateChange={onFormStateChange}
                  onStepStatus={onStepStatus}
                  onGoToSellerStep={() =>
                    navigateTo(PackageType.TRUST_DETAILS, TrustDetailsStep.SELLER_DETAILS)
                  }
                  onGoToFatcaCrsStep={() =>
                    navigateTo(PackageType.COMPLIANCE, ComplianceStep.FATCA_CRS)
                  }
                />
              )}
              {activeTab === PackageType.KYC && (
                <TrustAccountActivationKycForm
                  trustAccountId={trustAccountId}
                  activeStep={activeStep}
                  onFormStateChange={onFormStateChange}
                />
              )}
            </div>
          </div>
        </div>
      </MultiStepsModal>
    </ShowErrorsContext.Provider>
  );
};
